import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { startPoll, stopPoll, POLL_STATE } from "redux/actions/Polling";
import config from "lib/config";

import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
} from "@material-ui/core";

import Alert from "@material-ui/lab/Alert";

import { makeStyles } from "@material-ui/core/styles";

import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import EditIcon from "@material-ui/icons/Edit";

import { cloudModuleMetadata, INTEGRATION_STATUS } from "./constants";
import PropTypes from "prop-types";

import SimpleTable from "views/Components/SimpleTable";

import { Integration as IntegrationModel } from "lib/models/Integration";
import moment from "moment-timezone";
import { loadPageData } from "../../../redux/actions/Request";

import { IntegrationLogo } from "./IntegrationLogo";
import { ConfigurationForm } from "./ConfigurationForm";

import { CircularProgress } from "@material-ui/core";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";

import { renderModifiedCell } from "utils";

const useStyles = makeStyles((theme) => ({
  moduleOptionsContainer: {
    display: "flex",
    paddingBottom: 20,
  },
  moduleButton: {
    border: `1px solid ${theme.palette.divider}`,
    margin: 5,
    padding: 15,
  },
  moduleOptionContainer: {
    width: 250,
    height: 100,
    display: "flex",
    cursor: "pointer",
    flexDirection: "column",
    justifyContent: "space-between",
    borderRadius: theme.shape.borderRadius,
    textAlign: "left",
  },
  textContainer: {
    display: "flex",
    flexWrap: "no-wrap",
    flexDirection: "column",
  },
  moduleTitleText: {
    margin: 0,
  },
  moduleSubtitleText: {
    margin: 0,
    fontWeight: "bold",
  },
  form: {
    width: 600,
    paddingTop: 10,
    "& fieldset": {
      padding: `0 !important`,
    },
  },
  dialogActions: {
    padding: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dialogActionsBtnContainer: {
    display: "flex",
    alignItems: "center",
  },
  alert: {
    margin: "10px 0",
    backgroundColor: "#D9EBFF",
  },
  error: {
    color: theme.palette.error.main,
  },
  container: {
    display: "flex",
    alignItems: "center",
  },
  circle: {
    minWidth: 15,
    height: 15,
    marginRight: 10,
    borderRadius: "100%",
  },
  progress: {
    marginRight: 10,
  },
}));

const renderLogoCell = (column, model) => {
  return (
    <IntegrationLogo
      integrationType={model[column.field]}
      width="65"
      height="30"
    />
  );
};

const renderDateCell = (column, model) => {
  const timeValue = model[column.field];
  if (!timeValue) {
    return "Pending";
  }
  return moment.utc(timeValue).tz(moment.tz.guess(true)).format("lll z");
};

const CloudModules = (props) => {
  const classes = useStyles();

  const { integrations, orgColumnStats, ready, orgId } = props;

  const renderLogCountCell = (column, model) => {
    const thisOrgStats =
      orgColumnStats.find((stats) => stats.orgId === orgId) || {};
    const runningHistory = thisOrgStats.running_history || {};
    const runningRecent = thisOrgStats.running_recent || {};
    const historyLogs = (runningHistory[model.id] || [0])[0];
    const recentLogs = (runningRecent[model.id] || [0])[0];
    var logs = historyLogs + recentLogs;
    let n = 0;
    while (logs > 1000) {
      n += 1;
      logs = Math.floor(logs / 1000);
    }

    if (n === 0) {
      return logs;
    }

    if (n === 1) {
      return `${logs}K`;
    }

    if (n === 2) {
      return `${logs}M`;
    }

    if (n === 3) {
      return `${logs}B`;
    }

    if (n === 4) {
      return `${logs}T`;
    }
  };

  const renderStatusCell = (column, model) => {
    if (model[column.field] === INTEGRATION_STATUS.INIT) {
      return (
        <span className={classes.container}>
          <CircularProgress size={20} className={classes.progress} />
          <span>Initializing…</span>
        </span>
      );
    }

    if (model[column.field] === INTEGRATION_STATUS.OK) {
      return "Online";
    }

    if (model[column.field] === INTEGRATION_STATUS.ERROR) {
      if (model["integrationStatusMessage"]) {
        const errStr = "Error: ";
        const errMsg = <tt>{`${model["integrationStatusMessage"]}`}</tt>;
        return (
          <span>
            {errStr} {errMsg}
          </span>
        );
      } else {
        return "Error";
      }
    }

    return model[column.field];
  };

  const cloudModulesColumns = [
    {
      field: "integrationType",
      renderValue: renderLogoCell,
      searchable: false,
    },
    {
      title: "Cloud Connector Name",
      field: "integrationName",
      searchable: true,
    },
    {
      title: "Current Status",
      field: "integrationStatus",
      searchable: true,
      renderValue: renderStatusCell,
      icon: (model) => {
        if (model["integrationStatus"] === INTEGRATION_STATUS.INIT) {
          return null;
        } else {
          return (
            <FiberManualRecordIcon
              style={{
                color:
                  model["integrationStatus"] === INTEGRATION_STATUS.OK
                    ? "#3BB273"
                    : "#E76F51",
              }}
            />
          );
        }
      },
    },
    {
      title: "Total Logs",
      field: "totalLogs",
      searchable: false,
      renderValue: renderLogCountCell,
    },
    {
      title: "Last Status Update",
      field: "lastStatusUpdate",
      searchable: false,
      renderValue: renderDateCell,
    },
    {
      title: "Created",
      field: "created",
      searchable: false,
      renderValue: renderDateCell,
    },
    {
      title: "Last Modified",
      field: "modified",
      searchable: false,
      renderValue: renderModifiedCell,
    },
  ];

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedModel, setSelectedModel] = useState(null);
  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const [isErrorDisplayed, setIsErrorDisplayed] = useState(false);
  const [isModuleDialogOpen, setIsModuleDialogOpen] = useState(false);
  const [selectedCloudModule, setSelectedCloudModule] = useState(null);
  const [isConnectingOrSaving, setIsConnectingOrSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [pendingRemoveSelectedModule, setPendingRemoveSelectedModule] =
    useState(false);
  const [hasValidationError, setHasValidationError] = useState(false);

  useEffect(() => {
    // connectorIdFromPayload prop is taken from the 'id1' in the page's payload
    // i.e. for a clickthrough event from the summary dashboard
    if (props.connectorIdFromPayload && integrations && props.ready) {
      // find the full model to set in state for viewing
      const selectedIntegrationFromPayload = integrations.find(
        (integration) => integration.id === props.connectorIdFromPayload
      );

      // if a matching model is found open dialog for viewing
      if (selectedIntegrationFromPayload)
        handleRowClick(selectedIntegrationFromPayload);
    }
  }, [props.connectorIdFromPayload, integrations, props.ready]);

  useEffect(() => {
    // for each integration
    props.integrations.map((integration) => {
      if (
        integration.integrationStatus === INTEGRATION_STATUS.INIT ||
        integration.lastStatusUpdate === null
      ) {
        if (
          props.polls[integration.id] &&
          props.polls[integration.id].state === POLL_STATE.STOPPED
        ) {
          // if the integration status is initialize and the poll is already registered, but stopped
          // start the poll
          return props.startPoll(integration, 5);
        } else if (!props.polls[integration.id]) {
          // if the integration status is initialize and there is no poll registered
          // start the poll
          return props.startPoll(integration, 5);
        }
        return null;
      } else {
        if (
          props.polls[integration.id] &&
          props.polls[integration.id].state === POLL_STATE.RUNNING
        ) {
          // if the integration status isn't initialize but there is a running poll
          // stop the poll
          return props.stopPoll([integration.id]);
        }
        return null;
      }
    });
  }, [props.integrations, props.polls]);

  const toggleModal = () => {
    setIsErrorDisplayed(false);
    setIsConnectingOrSaving(false);
    setIsRemoving(false);
    setPendingRemoveSelectedModule(false);
    if (isModuleDialogOpen) {
      setTimeout(() => setCurrentStep(0), 200);
    }
    setIsModuleDialogOpen(!isModuleDialogOpen);
  };

  const handleNextStep = () => {
    let nextStep = currentStep + 1;
    setCurrentStep(nextStep);
  };

  const handleSelectModule = (module) => {
    setSelectedCloudModule(module);
    setSelectedModel(
      new IntegrationModel({
        integrationName: `${module.title} Cloud Connector ${moment()
          .format("MM/DD/YYYY")
          .replace(/\//g, "-")}`,
        integrationType: module.integrationType,
        configuration: {},
        secret: {},
      })
    );
    handleNextStep();
  };

  const handleRowClick = (model) => {
    let module = cloudModuleMetadata.find(
      ({ integrationType }) => integrationType === model.integrationType
    );
    setSelectedCloudModule(module);
    setSelectedModel(model);
    setCurrentStep(1);
    toggleModal();
  };

  const handleRemoveButton = () => {
    setIsErrorDisplayed(false);
    setIsConnectingOrSaving(false);
    setIsRemoving(false);
    setPendingRemoveSelectedModule(true);
  };

  const handleConfirmPendingDelete = () => {
    setIsErrorDisplayed(false);
    setIsConnectingOrSaving(false);
    setIsRemoving(true);
    selectedModel
      .delete()
      .then(() => {
        props.reload(true);
        toggleModal();
      })
      .catch((e) => {
        console.error(e);
        setIsErrorDisplayed(true);
        setIsConnectingOrSaving(false);
        setIsRemoving(false);
      });
  };

  const handleClearPendingDelete = () => {
    setIsErrorDisplayed(false);
    setIsConnectingOrSaving(false);
    setIsRemoving(false);
    setPendingRemoveSelectedModule(false);
  };

  const handleConnect = () => {
    const isUpdating = !!selectedModel.id;
    setIsErrorDisplayed(false);
    setIsConnectingOrSaving(true);
    setIsRemoving(false);
    const currentSelectedModelId = isUpdating ? selectedModel.id : null;
    const currentSelectedModelOrgId = isUpdating ? selectedModel.orgId : null;
    const func = isUpdating ? "update" : "create";
    selectedModel[func]()
      .then(() => {
        props.reload(true);
        toggleModal();
      })
      .catch((e) => {
        console.error(e);
        if (isUpdating) {
          selectedModel.set({
            id: currentSelectedModelId,
            orgId: currentSelectedModelOrgId,
          });
        }
        setIsErrorDisplayed(true);
        setIsConnectingOrSaving(false);
        setIsRemoving(false);
      });
  };

  const getConnectButtonText = () => {
    let textToReturn = isConnectingOrSaving ? "Connecting..." : "Connect";
    if (selectedModel && selectedModel.id)
      textToReturn = isConnectingOrSaving ? "Saving..." : "Save Changes";
    return textToReturn;
  };

  const getRemoveButtonText = () => {
    return isRemoving ? "Removing..." : "Remove";
  };

  const currentDialogStepTitle = () => {
    return currentStep === 0
      ? "Available Cloud Connectors"
      : selectedModel.id
      ? "Edit Cloud Connector"
      : "Add Cloud Connector";
  };

  const handleCancelModal = () => {
    if (selectedModel) {
      selectedModel.reset();
    }
    toggleModal();
  };
  const displayDialogActions = ({ hasValidationError }) => {
    if (currentStep > 0 && pendingRemoveSelectedModule) {
      return (
        <DialogActions className={classes.dialogActions}>
          <div className={classes.dialogActionsBtnContainer}>
            Are you sure you want to remove this cloud connector?
            <br />
            <Button
              datacy={"cloudModuleConfirmRemoveBtn"}
              onClick={handleConfirmPendingDelete}
              disabled={!!isRemoving}
              className={classes.error}
              variant={"text"}
              style={{ marginLeft: 5 }}
            >
              {isRemoving ? "Removing..." : "Confirm"}
            </Button>
            <Button
              onClick={handleClearPendingDelete}
              disabled={!!isRemoving}
              color={"primary"}
              variant={"contained"}
              style={{ marginLeft: 5 }}
            >
              Cancel
            </Button>
          </div>
        </DialogActions>
      );
    } else if (currentStep === 1) {
      return (
        <DialogActions className={classes.dialogActions}>
          <div className={classes.dialogActionsBtnContainer}>
            {selectedModel && selectedModel.id && (
              <Button
                datacy={"cloudModuleRemoveBtn"}
                disabled={!!isRemoving}
                onClick={handleRemoveButton}
                className={classes.error}
                variant={"text"}
                style={{ marginLeft: 5 }}
              >
                {getRemoveButtonText()}
              </Button>
            )}
            <Button
              onClick={handleCancelModal}
              color={"primary"}
              variant={"text"}
              style={{ marginLeft: 5 }}
            >
              Cancel
            </Button>
            <Button
              datacy={"cloudModuleConnectBtn"}
              disabled={!!isConnectingOrSaving || hasValidationError}
              onClick={handleConnect}
              color={"primary"}
              variant={"contained"}
              style={{ marginLeft: 5 }}
            >
              {getConnectButtonText()}
            </Button>
          </div>
          {selectedCloudModule && (
            <a
              href={selectedCloudModule.setupGuideLink}
              target={"_blank"}
              rel="noopener noreferrer"
            >
              {selectedCloudModule.title} setup guide <OpenInNewIcon />
            </a>
          )}
        </DialogActions>
      );
    }
    return null;
  };

  const CloudButton = (props) => {
    return (
      <Grid item xs={3}>
        <Button
          variant={"outlined"}
          disabled={props.disabled}
          className={classes.moduleButton}
          datacy={props.module.integrationType}
          onClick={() => handleSelectModule(props.module)}
        >
          <div className={classes.moduleOptionContainer}>
            {
              <IntegrationLogo
                integrationType={props.module.integrationType}
                width={props.module.imageWidth || 100}
                height={
                  props.module.imageWidth
                    ? props.module.imageWidth / 2.166666667
                    : 46
                }
              />
            }
            <div className={classes.textContainer}>
              <p className={classes.moduleTitleText}>{props.module.title}</p>
              <p className={classes.moduleSubtitleText}>
                {props.module.subtitle}
              </p>
            </div>
          </div>
        </Button>
      </Grid>
    );
  };

  const displayDialogContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Grid
            container
            spacing={2}
            className={classes.moduleOptionsContainer}
          >
            {cloudModuleMetadata
              .filter((module) =>
                config.cloud_connectors.includes(module.integrationType)
              )
              .map((module, idx) => {
                // Disable AWS connector on every license EXCEPT the ADVANCED license
                if (
                  module.integrationType === "aws/Initialize" &&
                  (props.license.isTagged("limited-to-cloud") ||
                    props.license.isTagged("limited-to-microsoft"))
                ) {
                  return (
                    <CloudButton
                      key={module.integrationType}
                      module={module}
                      disabled={true}
                    />
                  );
                  // Disable Duo, SentinelOne, Mimecast, and Umbrella connectors on FREE and M365 licenses
                } else if (
                  (module.integrationType === "sentinelone/poll" ||
                    module.integrationType === "duo/poll" ||
                    module.integrationType === "mimecast/poll" ||
                    module.integrationType === "umbrella/poll" ||
                    module.integrationType === "webroot/poll") &&
                  props.license.isTagged("limited-to-microsoft")
                ) {
                  return (
                    <CloudButton
                      key={module.integrationType}
                      module={module}
                      disabled={true}
                    />
                  );
                } else {
                  return (
                    <CloudButton
                      key={module.integrationType}
                      module={module}
                      disabled={false}
                    />
                  );
                }
              })}
          </Grid>
        );
      case 1:
        return (
          <div className={classes.moduleFormContainer}>
            {selectedCloudModule && (
              <IntegrationLogo
                integrationType={selectedCloudModule.integrationType}
                width={selectedCloudModule.imageWidth || 100}
                height={
                  selectedCloudModule.imageWidth
                    ? selectedCloudModule.imageWidth / 2.166666667
                    : 46
                }
              />
            )}
            {isAlertVisible &&
              selectedCloudModule &&
              !pendingRemoveSelectedModule &&
              !selectedModel.id && (
                <Alert
                  icon={false}
                  className={classes.alert}
                  onClose={() => setIsAlertVisible(false)}
                >
                  Get started by{" "}
                  <a
                    target={"_blank"}
                    rel="noopener noreferrer"
                    href={selectedCloudModule.setupGuideLink}
                  >
                    opening the {selectedCloudModule.title} setup guide.{" "}
                    <OpenInNewIcon />
                  </a>
                </Alert>
              )}
            {isErrorDisplayed && selectedCloudModule && (
              <p className={classes.error}>
                An error occurred trying to connect to{" "}
                {selectedCloudModule.title}.<br />
                Please ensure your entries are correct and try again.
              </p>
            )}
            <div className={classes.form}>
              <ConfigurationForm
                model={selectedModel}
                disabled={isConnectingOrSaving || isRemoving}
                onValidation={(validation) =>
                  setHasValidationError(validation.hasValidationError)
                }
                validateAll={isErrorDisplayed}
                onSubmit={handleConnect}
                schemas={props.integrationSchemas}
                superadmin={props.superadmin}
                selectedCloudModule={selectedCloudModule}
              />
            </div>
          </div>
        );
      default:
        return <span>error: unknown step {currentStep}</span>;
    }
  };

  return (
    <div>
      <SimpleTable
        isNorthStar
        data={integrations}
        isFetching={!ready}
        rowClick={handleRowClick}
        columns={cloudModulesColumns}
        emptyText={<span>No connectors</span>}
        licenseRestriction={"integration.create"}
        toolbarActions={[
          {
            icon: AddCircleIcon,
            tooltip: "Add Cloud Connector",
            onClick: toggleModal,
            disabled:
              props.license.isTagged("limited-to-microsoft") &&
              integrations?.length === 1
                ? true
                : false,
          },
        ]}
        actions={[
          {
            icon: EditIcon,
            tooltip: "Edit Cloud Connector",
            onClick: (event, model) => {
              handleRowClick(model);
            },
          },
        ]}
      />
      <Dialog
        open={isModuleDialogOpen}
        onClose={handleCancelModal}
        maxWidth={"lg"}
      >
        <DialogTitle>{currentDialogStepTitle()}</DialogTitle>
        <DialogContent>{displayDialogContent()}</DialogContent>
        {displayDialogActions({ hasValidationError })}
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    polls: state.request.polls,
    license: state.license,
    orgId: state.location.payload.orgId,
    connectorIdFromPayload: state.page.payload.id1,
    superadmin: state?.session?.settings?.user?.superadmin,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    reload: (force) => dispatch(loadPageData(force)),
    startPoll: (model, interval, autoResume) =>
      dispatch(startPoll(model, interval, autoResume)),
    stopPoll: (modelIds) => dispatch(stopPoll(modelIds)),
  };
};

CloudModules.propTypes = {
  integrations: PropTypes.array,
  integrationSchemas: PropTypes.array,
  orgColumnStats: PropTypes.array,
  ready: PropTypes.bool,
  superadmin: PropTypes.bool,
};

CloudModules.defaultProps = {
  integrations: [],
  integrationSchemas: [],
  orgColumnStats: [],
  ready: false,
  superadmin: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(CloudModules);
